import React from 'react';
import { Box, styled } from '@mui/system';
import { Typography, Grid, Paper, Chip, Divider, Avatar } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SportsScoreIcon from '@mui/icons-material/SportsScore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import { AISearchResult } from '../types';

const ResponseCard = styled(Paper)({
  padding: '24px',
  borderRadius: '16px',
  border: '1px solid #e2e8f0',
  boxShadow: '0 4px 20px rgba(148, 163, 184, 0.08)',
  backgroundColor: '#ffffff',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '4px',
    height: '100%',
    background: 'linear-gradient(180deg, #7c3aed 0%, #3b82f6 100%)',
  },
});

const ConfidenceBadge = styled(Box)<{ score: number }>(({ score }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '6px 12px',
  borderRadius: '20px',
  fontSize: '13px',
  fontWeight: 'bold',
  gap: '6px',
  backgroundColor: score > 0.9 ? '#ecfdf5' : '#fef3c7',
  color: score > 0.9 ? '#059669' : '#d97706',
  border: `1px solid ${score > 0.9 ? '#10b98133' : '#f59e0b33'}`,
}));

const MetricBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '12px 8px',
  borderRadius: '12px',
  backgroundColor: '#f8fafc',
  border: '1px solid #f1f5f9',
  textAlign: 'center',
  minWidth: '90px',
});

const UnderstandingItem = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '8px 12px',
  borderRadius: '8px',
  backgroundColor: '#f8fafc',
  border: '1px solid #f1f5f9',
});

interface AIResponseViewProps {
  result: AISearchResult;
  onClickFollowUp: (term: string) => void;
  onClear: () => void;
}

export default function AIResponseView({ result, onClickFollowUp, onClear }: AIResponseViewProps) {
  const { summary, confidence, interpretedQuery, matchedRecords, followUps } = result;

  // Extract status distributions from matched records
  const statusCounts = matchedRecords.reduce(
    (acc, rec) => {
      const status = String(rec.status || '').toLowerCase();
      if (status === 'delivered') acc.delivered++;
      else if (['shipment_exception', 'unsuccessful_delivery', 'pickup_failed'].includes(status)) acc.delayed++;
      else if (status === 'cancelled' || status === 'void') acc.cancelled++;
      else acc.inTransit++;
      return acc;
    },
    { delivered: 0, delayed: 0, inTransit: 0, cancelled: 0 },
  );

  // Group matched parameters for "AI understood" layout
  const paramsList = [
    { label: 'Status', value: interpretedQuery.status },
    { label: 'Carrier', value: interpretedQuery.carrier },
    { label: 'Date Range', value: interpretedQuery.dateRange },
    { label: 'Destination', value: interpretedQuery.destination },
    { label: 'Origin', value: interpretedQuery.origin },
    { label: 'Route Type', value: interpretedQuery.routeType },
    { label: 'COD', value: interpretedQuery.isCod ? 'COD Shipments Only' : null },
    { label: 'Min Weight', value: interpretedQuery.minWeight ? `>= ${interpretedQuery.minWeight}kg` : null },
    { label: 'Max Weight', value: interpretedQuery.maxWeight ? `<= ${interpretedQuery.maxWeight}kg` : null },
    { label: 'Updated By', value: interpretedQuery.updatedBy },
  ].filter(p => p.value != null);

  return (
    <Box sx={{ width: '100%', mb: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <ResponseCard>
        {/* Header with AI indicator and confidence score */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            flexWrap: 'wrap',
            gap: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: '#f5f3ff', width: 36, height: 36 }}>
              <AutoAwesomeIcon sx={{ color: '#7c3aed', fontSize: 18 }} />
            </Avatar>
            <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 700, color: '#1e1b4b' }}>
              AI Smart Search Results
            </Typography>
          </Box>

          <ConfidenceBadge score={confidence}>
            <CheckCircleIcon sx={{ fontSize: 16 }} />
            {Math.round(confidence * 100)}% Confidence
          </ConfidenceBadge>
        </Box>

        {/* Dynamic AI Summary section */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body1"
            sx={{ color: '#334155', fontWeight: 500, lineHeight: 1.6, fontSize: '15px', mb: 2 }}
          >
            {summary}
          </Typography>

          {/* Quick Metrics display */}
          <Grid container spacing={1.5}>
            <Grid item xs={6} sm={3}>
              <MetricBox>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, mb: 0.5 }}>
                  Found
                </Typography>
                <Typography variant="h6" sx={{ color: '#1e293b', fontWeight: 700 }}>
                  {matchedRecords.length}
                </Typography>
              </MetricBox>
            </Grid>
            <Grid item xs={6} sm={3}>
              <MetricBox>
                <Typography variant="caption" sx={{ color: '#059669', fontWeight: 600, mb: 0.5 }}>
                  Delivered
                </Typography>
                <Typography variant="h6" sx={{ color: '#059669', fontWeight: 700 }}>
                  {statusCounts.delivered}
                </Typography>
              </MetricBox>
            </Grid>
            <Grid item xs={6} sm={3}>
              <MetricBox>
                <Typography variant="caption" sx={{ color: '#3b82f6', fontWeight: 600, mb: 0.5 }}>
                  In Transit
                </Typography>
                <Typography variant="h6" sx={{ color: '#3b82f6', fontWeight: 700 }}>
                  {statusCounts.inTransit}
                </Typography>
              </MetricBox>
            </Grid>
            <Grid item xs={6} sm={3}>
              <MetricBox>
                <Typography variant="caption" sx={{ color: '#dc2626', fontWeight: 600, mb: 0.5 }}>
                  Delayed
                </Typography>
                <Typography variant="h6" sx={{ color: '#dc2626', fontWeight: 700 }}>
                  {statusCounts.delayed}
                </Typography>
              </MetricBox>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 2.5, borderColor: '#f1f5f9' }} />

        {/* AI understood your query */}
        <Box sx={{ mb: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{ color: '#475569', fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <QueryStatsIcon fontSize="small" sx={{ color: '#3b82f6' }} />
            AI Query Interpretation:
          </Typography>

          <Grid container spacing={1.5}>
            {paramsList.length > 0 ? (
              paramsList.map(param => (
                <Grid item xs={12} sm={6} key={param.label}>
                  <UnderstandingItem>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                      {param.label}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 600 }}>
                      {param.value}
                    </Typography>
                  </UnderstandingItem>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic', pl: 1 }}>
                  Listing all shipment history records (no filters parsed).
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      </ResponseCard>

      {/* Follow-up chips */}
      {followUps.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{ color: '#475569', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <SportsScoreIcon fontSize="small" sx={{ color: '#7c3aed' }} />
            Refine this search:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {followUps.map(chipText => (
              <Chip
                key={chipText}
                label={chipText}
                onClick={() => onClickFollowUp(chipText)}
                sx={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #7c3aed40',
                  color: '#7c3aed',
                  fontWeight: 600,
                  fontSize: '12px',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#7c3aed1a',
                    transform: 'scale(1.02)',
                  },
                  transition: 'all 0.2s ease',
                }}
              />
            ))}
            <Chip
              label="Reset AI Search"
              variant="outlined"
              onClick={onClear}
              sx={{
                borderColor: '#cbd5e1',
                color: '#64748b',
                fontWeight: 600,
                fontSize: '12px',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#f1f5f9',
                  borderColor: '#94a3b8',
                },
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}
